// /////////////////////////////////////////////////////////////
// [ファイル] useSfaSavedFilters.ts
// [概要] 案件条件リスト（保存済み検索条件）の API 呼び出しと型。
//        2026-11-05 に Firestore（HozenallCloud_Sfa_SavedFilters）から Cloud SQL
//        （trn_sfa_saved_filters / trn_sfa_saved_filter_shares）へ移行。
//
// 権限の考え方（バックエンド sfa_saved_filter_service と同じ）
// - 編集・削除は作成者のみ。共有先は適用と複製だけ
// - 作成者が退職した共有条件は、共有メンバーが「引き継ぐ」ことで作成者になれる
// - 管理者（全社閲覧権限）は共有条件すべてを見られ、削除と引き継ぎができる
// /////////////////////////////////////////////////////////////
import axios from 'axios';
import { ep } from '../api/endpoints';
import type { ProjectFilters, SortConfig } from './useProjectTable';

export type SavedFilterScope = 'personal' | 'shared';

export interface SavedFilterMember {
  user_id: string;
  name: string;      // 氏名（アカウントが無ければメールアドレス）
  retired: boolean;  // 退職済み（在職判定は mst_account）
}

export interface SfaSavedFilter {
  id: number;
  user_id: string;           // 作成者のメールアドレス
  name: string;
  filters: ProjectFilters;
  sort: SortConfig | null;
  columns: string[] | null;  // 統合版一覧の表示列（無い＝既定）
  scope: SavedFilterScope;
  transferred_from: string | null;
  transferred_at: string | null;
  created_at: string;        // 'YYYY-MM-DD HH:MM:SS' 文字列
  updated_at: string;
  creator_name: string;
  creator_retired: boolean;
  shared_with: SavedFilterMember[];
  is_owner: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_take_over: boolean;
}

export interface SaveSavedFilterInput {
  id?: number;
  name: string;
  filters: ProjectFilters;
  sort: SortConfig | null;
  columns?: string[] | null;
  scope: SavedFilterScope;
  shared_with: string[];     // scope='shared' の共有メンバー（メールアドレス。作成者はサーバー側で必ず含める）
}

// 管理者扱いにする権限（全社閲覧）。画面の権限一覧から判定して API へ渡す
export const SAVED_FILTER_ADMIN_PERMISSION = 'readdata.sfa.all_view';
export const isSavedFilterAdmin = (permissions: string[] | null | undefined): boolean =>
  !!permissions?.includes(SAVED_FILTER_ADMIN_PERMISSION);

/** 'YYYY-MM-DD HH:MM:SS' → 'YYYY/MM/DD'（無ければ '---'） */
export const formatSavedFilterDate = (text: string | null | undefined): string =>
  text ? text.slice(0, 10).replace(/-/g, '/') : '---';

async function post<T = any>(name: string, body: any): Promise<T> {
  try {
    const res = await axios.post(ep(name), body);
    if (res.data?.status !== 'success') throw new Error(res.data?.message || '処理に失敗しました');
    return res.data as T;
  } catch (e: any) {
    const msg = e?.response?.data?.message || e?.message || '処理に失敗しました';
    throw new Error(msg);
  }
}

export const sfaSavedFilterApi = {
  list: (userEmail: string, isAdmin: boolean) =>
    post<{ rows: SfaSavedFilter[] }>('list_sfa_saved_filters', { userEmail, isAdmin }),
  save: (userEmail: string, input: SaveSavedFilterInput) =>
    post<{ row: SfaSavedFilter }>('save_sfa_saved_filter', { userEmail, ...input }),
  remove: (userEmail: string, id: number, isAdmin: boolean) =>
    post<{ id: number }>('delete_sfa_saved_filter', { userEmail, id, isAdmin }),
  takeOver: (userEmail: string, id: number, isAdmin: boolean) =>
    post<{ row: SfaSavedFilter }>('take_over_sfa_saved_filter', { userEmail, id, isAdmin }),
};
